import type { TaskDTO, TaskListQuery, TaskViewMode } from '@taskos/shared';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router';
import { useMonthDays, useTasks } from '../../api/queries';
import { Button, Empty, ErrorState, Skeleton } from '../../components/ui';
import { formatDate, todayISO } from '../../lib/format';
import { isClosed, TaskRow, TaskTile } from './TaskItems';

/** Arrow-key navigation between task items inside a container (no page scrolling). */
export function useRovingKeys() {
  const ref = useRef<HTMLDivElement>(null);
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const keys = ['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
    if (!keys.includes(e.key)) return;
    const target = e.target as HTMLElement;
    if (!target.dataset.taskId) return; // only when an item itself has focus
    const items = [...(ref.current?.querySelectorAll<HTMLElement>('[data-task-id]') ?? [])];
    const i = items.indexOf(target);
    if (i < 0) return;
    e.preventDefault();
    let next = i;
    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') next = Math.min(items.length - 1, i + 1);
    if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') next = Math.max(0, i - 1);
    if (e.key === 'Home') next = 0;
    if (e.key === 'End') next = items.length - 1;
    items[next]?.focus();
    items[next]?.scrollIntoView({ block: 'nearest' });
  };
  return { ref, onKeyDown };
}

export function TaskCollection({
  items,
  mode,
  selectable,
  selected,
  onToggleSelect,
  emptyTitle,
  emptyIcon,
  emptyText,
  baseQuery,
}: {
  items: TaskDTO[];
  mode: TaskViewMode;
  selectable?: boolean;
  selected?: Set<string>;
  onToggleSelect?: (id: string) => void;
  emptyTitle: string;
  emptyIcon?: string;
  emptyText?: string;
  baseQuery?: Partial<TaskListQuery>;
}) {
  const roving = useRovingKeys();
  if (mode === 'CALENDAR') return <CalendarView baseQuery={baseQuery ?? {}} />;
  if (!items.length) return <Empty icon={emptyIcon ?? '🎯'} title={emptyTitle}>{emptyText}</Empty>;

  if (mode === 'TILES')
    return (
      <div className="grid-tiles" ref={roving.ref} onKeyDown={roving.onKeyDown} role="list">
        {items.map((t) => (
          <div role="listitem" key={t.id}>
            <TaskTile t={t} selected={selected?.has(t.id)} />
          </div>
        ))}
      </div>
    );

  if (mode === 'TIMELINE') return <TimelineView items={items} />;

  return (
    <div className="list" ref={roving.ref} onKeyDown={roving.onKeyDown} role="grid" aria-label="Tasks">
      {items.map((t) => (
        <TaskRow key={t.id} t={t} selectable={selectable} checked={selected?.has(t.id)} selected={selected?.has(t.id)} onToggleSelect={() => onToggleSelect?.(t.id)} />
      ))}
    </div>
  );
}

function TimelineView({ items }: { items: TaskDTO[] }) {
  const groups = useMemo(() => {
    const m = new Map<string, TaskDTO[]>();
    for (const t of [...items].sort((a, b) => (a.dueDate ?? '9999').localeCompare(b.dueDate ?? '9999'))) {
      const k = t.dueDate ?? 'none';
      m.set(k, [...(m.get(k) ?? []), t]);
    }
    return [...m.entries()];
  }, [items]);
  const roving = useRovingKeys();
  const today = todayISO();
  return (
    <div ref={roving.ref} onKeyDown={roving.onKeyDown}>
      {groups.map(([date, list]) => (
        <section className="tl-group" key={date}>
          <h3>
            {date === 'none' ? 'No date' : formatDate(date)}
            {date !== 'none' && date < today && list.some((t) => !isClosed(t)) && <span className="badge danger" style={{ marginLeft: 8 }}>Overdue</span>}
            {date === today && <span className="badge primary" style={{ marginLeft: 8 }}>Today</span>}
          </h3>
          <div className="list">
            {list.map((t) => (
              <TaskRow key={t.id} t={t} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

const WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function CalendarView({ baseQuery }: { baseQuery: Partial<TaskListQuery> }) {
  const nav = useNavigate();
  const today = todayISO();
  const [cursor, setCursor] = useState(() => ({ y: Number(today.slice(0, 4)), m: Number(today.slice(5, 7)) }));
  const first = `${cursor.y}-${String(cursor.m).padStart(2, '0')}-01`;
  const daysInMonth = new Date(Date.UTC(cursor.y, cursor.m, 0)).getUTCDate();
  const last = `${cursor.y}-${String(cursor.m).padStart(2, '0')}-${String(daysInMonth).padStart(2, '0')}`;
  const q = useTasks({ ...baseQuery, view: baseQuery.view === 'overdue' || baseQuery.view === 'today' || baseQuery.view === 'upcoming' ? 'all' : (baseQuery.view ?? 'all'), dueFrom: first, dueTo: last, limit: 200, cursor: undefined, sort: 'due', dir: 'asc' });
  const days = useMonthDays(cursor.y, cursor.m);
  const off = new Map((days.data ?? []).map((d) => [d.date, d]));

  const leading = (new Date(Date.UTC(cursor.y, cursor.m - 1, 1)).getUTCDay() + 6) % 7; // Monday-first
  const cells: Array<string | null> = [...Array(leading).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => `${first.slice(0, 8)}${String(i + 1).padStart(2, '0')}`)];
  while (cells.length % 7) cells.push(null);
  const byDate = new Map<string, TaskDTO[]>();
  for (const t of q.data?.items ?? []) if (t.dueDate) byDate.set(t.dueDate, [...(byDate.get(t.dueDate) ?? []), t]);
  const monthName = new Date(Date.UTC(cursor.y, cursor.m - 1, 1)).toLocaleString('en-IN', { month: 'long', year: 'numeric', timeZone: 'UTC' });
  const move = (delta: number) =>
    setCursor((c) => {
      const t = c.y * 12 + (c.m - 1) + delta;
      return { y: Math.floor(t / 12), m: (t % 12) + 1 };
    });

  return (
    <div>
      <div className="inline-row mb">
        <Button size="sm" iconOnly icon={<ChevronLeft size={16} />} aria-label="Previous month" onClick={() => move(-1)} />
        <strong style={{ minWidth: 150, textAlign: 'center' }}>{monthName}</strong>
        <Button size="sm" iconOnly icon={<ChevronRight size={16} />} aria-label="Next month" onClick={() => move(1)} />
        <Button size="sm" onClick={() => setCursor({ y: Number(today.slice(0, 4)), m: Number(today.slice(5, 7)) })}>
          Today
        </Button>
        {q.isFetching && <span className="muted small">Loading…</span>}
      </div>
      {q.isError ? (
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      ) : q.isPending ? (
        <Skeleton rows={5} height={90} />
      ) : (
        <div className="cal" role="grid" aria-label={monthName}>
          {WEEK.map((d) => (
            <div key={d} className="cal-head" role="columnheader">
              {d}
            </div>
          ))}
          {cells.map((d, i) => {
            if (!d) return <div key={`e${i}`} className="cal-day other" aria-hidden />;
            const info = off.get(d);
            const list = byDate.get(d) ?? [];
            return (
              <div key={d} className={`cal-day ${d === today ? 'today' : ''} ${info && !info.working ? 'off' : ''}`} role="gridcell" title={info?.holidayName ?? (info && !info.working ? 'Non-working day' : undefined)}>
                <span className="cal-num">{Number(d.slice(8))}</span>
                {info?.holidayName && <span className="small muted truncate">🎉 {info.holidayName}</span>}
                {list.slice(0, 4).map((t) => (
                  <button key={t.id} type="button" className={`cal-task ${t.priority}`} onClick={() => nav(`/tasks/${t.id}`)} title={t.title}>
                    {t.title}
                  </button>
                ))}
                {list.length > 4 && <span className="small muted">+{list.length - 4} more</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
